import { SensorFamilyEnum } from '@/types/sensor/sensor.types';

import { CameraType, NeutralSensorWithSectorsType, SensorConfigurationByType } from './configuration.types';
import { EquipmentConfiguration, EquipmentStatus, PerimeterCamera, SensorTypeEnum } from './sensors';
import { SensorStatusByType } from './status.types';

export type InactiveMonitoringSensorByType<T extends SensorTypeEnum> = {
  type: T;
  active: false;
  configuration: SensorConfigurationByType<T>;
  status: null;
};
export type ActiveMonitoringSensorByType<T extends SensorTypeEnum> = {
  type: T;
  active: true;
  configuration: SensorConfigurationByType<T>;
  status: SensorStatusByType<T>;
};

export type MonitoringSensorByType<T extends SensorTypeEnum> =
  | InactiveMonitoringSensorByType<T>
  | ActiveMonitoringSensorByType<T>;

export type NeutralActiveMonitoringWithSectors = ActiveMonitoringSensorByType<NeutralSensorWithSectorsType>;
export type CameraActiveMonitoringSensor = ActiveMonitoringSensorByType<CameraType>;

export type InactiveMonitoringSensor = InactiveMonitoringSensorByType<SensorTypeEnum>;
export type ActiveMonitoringSensor = ActiveMonitoringSensorByType<SensorTypeEnum>;
export type MonitoringSensor = MonitoringSensorByType<SensorTypeEnum>;

export interface InactiveMonitoringEquipment {
  configuration: EquipmentConfiguration;
}

export type MonitoringEquipment = InactiveMonitoringEquipment | EquipmentStatus;

export enum StatusEnum {
  HEALTHY = 'HEALTHY',
  DEGRADED = 'DEGRADED',
  UNHEALTHY = 'UNHEALTHY',
}

export interface SystemStatus {
  memoryUsage: number;
  uptime: number;
}

export interface SpecificStatus {
  type: string;
}

export interface SensorsSpecificStatus extends SpecificStatus {
  sensors: MonitoringSensor[];
  activeEquipments: EquipmentStatus[];
  inactiveEquipments: InactiveMonitoringEquipment[];
  perimeterCameras: PerimeterCamera[];
}

export interface AppStatus {
  app: string;
  appVersion: string;
  status: StatusEnum;
  systemStatus?: SystemStatus;
  specificStatus?: SpecificStatus;
}

export enum LicenceType {
  PERPETUAL = 'PERPETUAL',
  EXECUTIONS = 'EXECUTIONS',
  EXPIRATION = 'EXPIRATION',
  CONCURRENCY = 'CONCURRENCY',
  TRIAL = 'TRIAL',
}

export type Licence =
  | {
      type: LicenceType.TRIAL | LicenceType.EXPIRATION;
      expirationDate: string;
      counter?: never;
      counterMax?: never;
    }
  | {
      type: LicenceType.EXECUTIONS | LicenceType.CONCURRENCY;
      expirationDate?: never;
      counter: number;
      counterMax: number;
    }
  | {
      type: LicenceType.PERPETUAL;
      expirationDate?: never;
      counter?: never;
      counterMax?: never;
    };

export enum ProtectionLevel {
  L1 = 'L1',
  L2 = 'L2',
  L3 = 'L3',
  L4 = 'L4',
  VMS = 'VMS',
}

export interface SensorFamilyLicence {
  family: SensorFamilyEnum;
  counter?: number;
  counterMax?: number;
}

export interface SensorLicence {
  availableFamilies: SensorFamilyLicence[];
}

export interface Options {
  data: boolean;
  replay: boolean;
  utm: boolean;
}

export interface C2Licence {
  key: string;
  licence: Licence;
  protection: ProtectionLevel;
  sensorLicence: SensorLicence;
  options: Options;
}

export interface Monitoring {
  totalMemoryUsage: number;
  appStatuses: AppStatus[];
  licence: C2Licence;
  monitoringSensors: SensorsSpecificStatus;
}
